import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { formatDateForDisplay } from "@/shared/utils";
import type { BudgetItem, BudgetSubcategory } from "../../../types";
import { formatCurrencyNoDecimals, parseMoney } from "../../../utils";
import { getLocalizedText } from "../../../utils/localization";

interface Props {
  item: BudgetItem;
  subcategory: BudgetSubcategory;
  iconBgClassName: string;
  icon: React.ReactNode;
}

export default function ItemDetailPanel({ item, subcategory, iconBgClassName, icon }: Props) {
  const { i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";

  const groupItems = useMemo(() => {
    return subcategory.items.filter((i) => i.group_id === item.group_id);
  }, [subcategory.items, item.group_id]);

  const groupTotal = useMemo(() => {
    return groupItems.reduce((sum, i) => sum + parseMoney(i.amount), 0);
  }, [groupItems]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col items-center gap-3 px-7 py-5 border-b surface-base-stroke">
        {icon && (
          <div
            className={cn(
              "w-10 aspect-square flex justify-center items-center radius-xs border surface-base-stroke",
              iconBgClassName,
            )}>
            {icon}
          </div>
        )}
        <div className="text-body-bold-lg content-base-primary text-center">
          {getLocalizedText(item.group_name, locale).split(" - ")[0]}
        </div>
        <div className="text-body-regular-sm content-base-secondary">{formatDateForDisplay(item.date)}</div>
        <div className="text-display-sm content-base-primary">- {formatCurrencyNoDecimals(groupTotal)}</div>
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto">
        {groupItems.map((groupItem, index) => (
          <div
            key={`${groupItem.source_id}-${index}`}
            className="flex items-center justify-between px-7 py-3 border-b surface-base-stroke">
            <div className="flex flex-col gap-0.5">
              <span className="text-label-md content-base-primary">{getLocalizedText(groupItem.name, locale)}</span>
              <span className="text-body-regular-xs content-base-secondary">{groupItem.account}</span>
            </div>
            <span className="text-body-bold-md content-base-primary shrink-0">
              - {formatCurrencyNoDecimals(groupItem.amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
